// Footer newsletter signup. Subscribes the email to the main Klaviyo
// newsletter list through the one shared client-subscribe implementation in
// email-capture-modal.ts. The form only renders when
// KLAVIYO_NEWSLETTER_LIST_ID is configured (see layout.html).

import { subscribeToKlaviyo, trackEvent } from './email-capture-modal';

class NewsletterForm {
  private readonly companyId: string;
  private readonly listId: string;
  private readonly input: HTMLInputElement;
  private readonly submitButton: HTMLButtonElement;
  private readonly errorInvalid: HTMLElement | null;
  private readonly errorNetwork: HTMLElement | null;
  private readonly successState: HTMLElement | null;
  private submitting = false;

  constructor(private readonly form: HTMLFormElement) {
    const input = form.querySelector<HTMLInputElement>('input[type="email"]');
    const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (!input || !submitButton) {
      throw new Error('[newsletter-form] form is missing its email input or submit button.');
    }
    this.companyId = form.dataset.klaviyoCompanyId ?? '';
    this.listId = form.dataset.klaviyoListId ?? '';
    this.input = input;
    this.submitButton = submitButton;
    this.errorInvalid = form.querySelector<HTMLElement>('[data-newsletter-error-invalid]');
    this.errorNetwork = form.querySelector<HTMLElement>('[data-newsletter-error-network]');
    // The success block is the form's sibling so the whole form can disappear.
    this.successState = form.parentElement?.querySelector<HTMLElement>('[data-newsletter-success]') ?? null;

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submit();
    });
  }

  private async submit(): Promise<void> {
    if (this.submitting) return;
    this.hideErrors();

    if (!this.input.checkValidity()) {
      if (this.errorInvalid) this.errorInvalid.hidden = false;
      this.input.focus();
      return;
    }

    this.submitting = true;
    this.submitButton.disabled = true;
    this.submitButton.classList.add('is-loading');

    try {
      await subscribeToKlaviyo({
        companyId: this.companyId,
        listId: this.listId,
        email: this.input.value.trim(),
        customSource: 'Footer newsletter',
      });
      this.form.hidden = true;
      if (this.successState) {
        this.successState.hidden = false;
        this.successState.focus();
      }
      trackEvent('newsletter-signup');
    } catch {
      if (this.errorNetwork) this.errorNetwork.hidden = false;
    } finally {
      this.submitting = false;
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('is-loading');
    }
  }

  private hideErrors(): void {
    if (this.errorInvalid) this.errorInvalid.hidden = true;
    if (this.errorNetwork) this.errorNetwork.hidden = true;
  }
}

function initNewsletterForm(): void {
  document
    .querySelectorAll<HTMLFormElement>('[data-newsletter-form]')
    .forEach((form) => new NewsletterForm(form));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNewsletterForm);
} else {
  initNewsletterForm();
}
